/**
 * "Change history" — who changed routing (a notification) or a pause, when, and what, one line each:
 * "Asha Patel changed "Fire in the warehouse": who is told · 2 Nov, 14:32". Closed until opened;
 * read when opened (and on Reload).
 */
import { useCallback, useState } from 'react';
import { ArrowPathIcon, ChevronDownIcon, ChevronRightIcon, ClockIcon } from '@heroicons/react/24/outline';
import { alertActionsErrorText, type PolicyChange } from './alertActionsModel';

export interface ChangeHistoryProps {
  load: (opts?: { entity?: 'policy' | 'silence'; limit?: number }) => Promise<PolicyChange[]>;
  entity?: 'policy' | 'silence';
  title?: string;
}

const VERB: Record<PolicyChange['action'], string> = {
  created: 'added', updated: 'changed', deleted: 'deleted', reordered: 'reordered', cancelled: 'ended',
};

function line(c: PolicyChange) {
  const who = c.changed_by || 'Someone';
  const what = c.entity === 'silence' ? `the pause "${c.name || ''}"` : c.action === 'reordered' ? `the order of the ${c.kind === 'system' ? 'system' : 'AI'} notifications` : `"${c.name || 'a notification'}"`;
  const detail = c.action === 'updated' && c.changes.length ? `: ${c.changes.join(', ')}` : '';
  return <><b>{who}</b> {VERB[c.action] || c.action} {what}{detail}</>;
}

export default function ChangeHistory({ load, entity, title = 'Change history' }: ChangeHistoryProps) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<PolicyChange[] | null>(null);
  const [error, setError] = useState('');

  const read = useCallback(async () => {
    setError('');
    try {
      setRows(await load({ entity, limit: 50 }));
    } catch (err) {
      setError(alertActionsErrorText(err, 'Could not read the history'));
      setRows([]);
    }
  }, [load, entity]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && rows === null) read();
  };

  return (
    <section className="rounded-xl border border-border bg-card">
      <div className="flex items-center gap-2 px-4 py-2.5">
        <button type="button" onClick={toggle} className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-[14px] font-semibold text-foreground">
          {open ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
          <ClockIcon className="h-4 w-4 text-muted-foreground" />
          {title}
        </button>
        {open && (
          <button type="button" onClick={read} className="rounded-lg border border-border p-1 text-muted-foreground hover:bg-muted" title="Reload">
            <ArrowPathIcon className="h-4 w-4" />
          </button>
        )}
      </div>
      {open && (
        <div className="border-t border-border px-4 py-2">
          {error && <p className="py-1 text-[12px] text-destructive">{error}</p>}
          {rows === null && <p className="py-1 text-[12px] text-muted-foreground">Loading…</p>}
          {rows && rows.length === 0 && !error && <p className="py-1 text-[12px] text-muted-foreground">No changes recorded yet.</p>}
          <ul className="divide-y divide-border">
            {(rows || []).map((c) => (
              <li key={c.id} className="flex flex-wrap items-baseline gap-x-3 py-1.5 text-[13px] text-foreground">
                <span className="min-w-0 flex-1">{line(c)}</span>
                <span className="shrink-0 text-[11px] text-muted-foreground">{new Date(c.at).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
